import { Currency, CurrencyCode, GeocodeAddress } from '@citruscamps/citrus-client'
import Dinero from 'dinero.js'
import { Address } from './address'

export const toCurrency = ({
  amount = 0,
  currency,
  language,
}: {
  amount: number
  currency: string
  language: string
}): Currency => {
  const precision: number = Dinero({
    amount: 0,
    currency: currency.toUpperCase() as Dinero.Currency,
  }).getPrecision()
  amount *= Math.pow(10, precision)
  const value: Dinero.Dinero = Dinero({
    amount: Math.round(amount),
    currency: currency.toUpperCase() as Dinero.Currency,
  }).setLocale(language)
  return {
    value: value.getAmount(),
    currency_code: value.getCurrency().toLowerCase() as CurrencyCode,
    major_value: value.toUnit(),
    display: value.setLocale(language).toFormat(),
    language,
  }
}

export const toCurrencyPrefix = (currency: CurrencyCode): string => {
  let value: Dinero.Dinero = Dinero({
    amount: 0,
    currency: currency.toUpperCase() as Dinero.Currency,
  })
  value = value.convertPrecision(-1 * value.getPrecision())
  return value.toFormat('$0').replace(/[\w_]+/g, '')
}

export const addCurrencies = (firstCurrency: Currency, ..._currencies: Currency[]): Currency => {
  const currency = firstCurrency.currency_code.toUpperCase() as Dinero.Currency
  const language = firstCurrency.language

  const currencies = [firstCurrency, ..._currencies.filter((c) => !!c)]
  const value: Dinero.Dinero = Dinero({
    amount: currencies.map((c) => c.value).reduce((a, b) => a + b, 0),
    currency,
  }).setLocale(language)
  return {
    value: value.getAmount(),
    currency_code: value.getCurrency().toLowerCase() as CurrencyCode,
    major_value: value.toUnit(),
    display: value.setLocale(language).toFormat(),
    language: firstCurrency.language,
  }
}

export const subtractCurrencies = (
  firstCurrency: Currency,
  ..._currencies: Currency[]
): Currency => {
  const currency = firstCurrency.currency_code.toUpperCase() as Dinero.Currency
  const language = firstCurrency.language
  const currencies = [firstCurrency, ..._currencies.filter((c) => !!c)]
  const value: Dinero.Dinero = Dinero({
    amount: currencies.map((c) => c.value).reduce((a, b) => (a === 0 ? b : a - b), 0),
    currency,
  }).setLocale(language)
  return {
    value: value.getAmount(),
    currency_code: value.getCurrency().toLowerCase() as CurrencyCode,
    major_value: value.toUnit(),
    display: value.setLocale(language).toFormat(),
    language,
  }
}

export const minCurrency = (firstCurrency: Currency, ..._currencies: Currency[]): Currency => {
  const currency = firstCurrency.currency_code.toUpperCase() as Dinero.Currency
  const language = firstCurrency.language
  const currencies = [firstCurrency, ..._currencies.filter((c) => !!c)]
  const value: Dinero.Dinero = Dinero({
    amount: Math.min(...currencies.map((c) => c.value)),
    currency,
  }).setLocale(language)
  return {
    value: value.getAmount(),
    currency_code: value.getCurrency().toLowerCase() as CurrencyCode,
    major_value: value.toUnit(),
    display: value.setLocale(language).toFormat(),
    language,
  }
}

export const inverseCurrency = (initialValue: Currency): Currency => {
  const inverse = toCurrency({
    amount: initialValue.major_value * -1 || 0,
    currency: initialValue.currency_code,
    language: initialValue.language,
  })
  return inverse
}

export const getCurrencyCodeFromAddress = (address: GeocodeAddress): CurrencyCode => {
  const parsedAddress = Address.fromGeocode(address)
  switch (parsedAddress.country_short_code) {
    case 'CA':
      return CurrencyCode.cad
    case 'US':
    default:
      return CurrencyCode.usd
  }
}
